import { useState, useRef, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  useTheme,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { motion, AnimatePresence } from "framer-motion";
import {
  DeleteConfirmDialog,
  PreviewDialog,
  ImagePreviewOverlay,
  ImageCard,
  VideoCard,
  DocumentCard,
} from "../components";

const MotionBox = motion.create(Box);
const MotionCard = motion.create(Card);
const MAX_SIZE = 20 * 1024 * 1024;

const API_BASE = process.env.REACT_APP_API_URL;

const getFileCategory = (f) => {
  if (f.type.includes("image")) return "images";
  if (f.type.includes("video")) return "videos";
  if (f.type.includes("audio")) return "videos";
  return "documents";
};

const attachmentUrl = (a) =>
  a.url ? (a.url.startsWith("http") ? a.url : `${API_BASE}${a.url}`) : null;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

export default function Attachments({ attachments = [], onUpload, onDelete }) {
  const inputRef = useRef(null);
  const theme = useTheme();

  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fullscreenPreview, setFullscreenPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tabLabels = ["Images", "Videos", "Documents"];

  const loadPreview = async (file, url) => {
    if (file.type.includes("pdf") && url.startsWith("http")) {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreview({ file, url: blobUrl });
    } else {
      setPreview({ file, url });
    }
  };

  useEffect(() => {
    setFiles(
      attachments.map((a) => ({
        id: a._id,
        file: {
          name: a.name,
          size: a.size,
          type: a.type,
        },
        url: attachmentUrl(a),
        remote: true,
      }))
    );
  }, [attachments]);

  const handleSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    for (const f of selected) {
      if (f.size > MAX_SIZE) {
        setError("File size must be below 20MB");
        return;
      }
    }
    setError(null);
    setFiles((p) => [
      ...p,
      ...selected.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        url: URL.createObjectURL(f),
        remote: false,
      })),
    ]);
  };

  const handleDeleteClick = (file) => {
    setDeleteConfirm(file);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setFiles((p) => p.filter((f) => f.id !== deleteConfirm.id));
    if (deleteConfirm.remote) {
      onDelete?.(deleteConfirm.id);
    }
    setDeleteConfirm(null);
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleUpload = async () => {
    const local = files.filter((f) => !f.remote);
    if (!local.length) return;
    await onUpload(local.map((f) => f.file));
    setFiles((p) => p.filter((f) => f.remote));
  };

  const grouped = files.reduce(
    (acc, f) => {
      const category = getFileCategory(f.file);
      acc[category].push(f);
      return acc;
    },
    { images: [], videos: [], documents: [] }
  );

  useEffect(() => {
    if (grouped.images.length > 0) {
      setActiveTab(0);
    } else if (grouped.videos.length > 0) {
      setActiveTab(1);
    } else if (grouped.documents.length > 0) {
      setActiveTab(2);
    }
  }, [files]);

  const renderSectionContent = () => {
    if (activeTab === 0) {
      return (
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.5,
          }}
        >
          <AnimatePresence>
            {grouped.images.map((item) => (
              <ImageCard
                key={item.id}
                {...item}
                onDelete={handleDeleteClick}
                onView={() => setFullscreenPreview({ file: item.file, url: item.url })}
                theme={theme}
              />
            ))}
          </AnimatePresence>
        </MotionBox>
      );
    }

    if (activeTab === 1) {
      return (
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1.5,
          }}
        >
          <AnimatePresence>
            {grouped.videos.map((item) => (
              <VideoCard
                key={item.id}
                {...item}
                onDelete={handleDeleteClick}
                onPreview={() => loadPreview(item.file, item.url)}
              />
            ))}
          </AnimatePresence>
        </MotionBox>
      );
    }

    if (activeTab === 2) {
      return (
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1.5,
          }}
        >
          <AnimatePresence>
            {grouped.documents.map((item) => (
              <DocumentCard
                key={item.id}
                {...item}
                onDelete={handleDeleteClick}
                onPreview={() => loadPreview(item.file, item.url)}
              />
            ))}
          </AnimatePresence>
        </MotionBox>
      );
    }

    return null;
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <MotionCard
        sx={{
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Attachments
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={() => inputRef.current?.click()}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: "#fff",
                borderRadius: 4,
                px: 2.5,
                py: 1,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                boxShadow: "0 4px 14px primary.main",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: theme.palette.primary,
                  transform: "translateY(-2px)",
                },
              }}
            >
              Upload Media
            </Button>
            <input
              ref={inputRef}
              hidden
              multiple
              type="file"
              onChange={handleSelect}
            />
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                animation: "shake 0.5s ease-in-out",
                "@keyframes shake": {
                  "0%, 100%": { transform: "translateX(0)" },
                  "25%": { transform: "translateX(-5px)" },
                  "75%": { transform: "translateX(5px)" },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: "1px solid #F0F0F0", mb: 2.5 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                minHeight: 40,
                "& .MuiTabs-indicator": {
                  bgcolor: theme.palette.primary.main,
                  height: 2.5,
                  borderRadius: 2,
                },
              }}
            >
              {tabLabels.map((label) => (
                <Tab
                  key={label}
                  label={label}
                  sx={{
                    textTransform: "capitalize",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#9CA3AF",
                    minHeight: 40,
                    px: 0,
                    mr: 3,
                    transition: "color 0.2s ease",
                    "&.Mui-selected": {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Content */}
          <Box
            sx={{
              maxHeight: 480,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#E5E7EB",
                borderRadius: 4,
                "&:hover": {
                  backgroundColor: "#D1D5DB",
                },
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
            }}
          >
            <AnimatePresence mode="wait">
              <MotionBox
                key={activeTab}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                {renderSectionContent()}
              </MotionBox>
            </AnimatePresence>
          </Box>

          {/* Upload pending files button */}
          {files.some((f) => !f.remote) && (
            <MotionBox
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}
            >
              <Button
                variant="contained"
                onClick={handleUpload}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: 6,
                  px: 3,
                  py: 1,
                  fontSize: 13,
                  fontWeight: 600,
                  boxShadow: "0 4px 14px primary.main",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: theme.palette.primary,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Upload Files
              </Button>
            </MotionBox>
          )}
        </CardContent>
      </MotionCard>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={!!deleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
      />

      {/* Preview Dialog (for videos/documents) */}
      <PreviewDialog preview={preview} onClose={() => setPreview(null)} />

      {/* Image Preview Overlay Popup */}
      <ImagePreviewOverlay
        preview={fullscreenPreview}
        onClose={() => setFullscreenPreview(null)}
      />
    </MotionBox>
  );
}
